import { Component, ElementRef, Inject } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { stripeConnectRequiredFields } from '../../settings/settings.service'

export type StripeBusinessType = 'individual' | 'company'

/**
 * Two independent ways a merchant can accept Stripe payments — see
 * stacks-agents' stripe.provider.ts for the matching backend branch:
 *  - 'byok'    ("bring your own key") — the merchant pastes their own Stripe secret
 *              key and Stacks takes no cut. The only option available to merchants
 *              whose business isn't registered in a Stripe Connect-supported country.
 *  - 'connect' — full Stripe Connect onboarding (KYC, destination charges, platform
 *              commission). Requires a Stripe-supported business country.
 */
export type StripeConnectionMode = 'byok' | 'connect'

export interface StripeSettingsDialogData {
  connectionMode?: StripeConnectionMode
  stripApiKey: string
  stripePublishableKey: string
  stripeWebhookSecret: string
  isEnabled?: boolean
  /** True while the parent is still fetching+decrypting previously saved settings. */
  isLoading?: boolean
  businessType?: StripeBusinessType
  country?: string
  // Individual requirements (Stripe `identity.individual` object)
  individual?: {
    givenName?: string
    surname?: string
    dateOfBirth?: string
    addressLine1?: string
    addressCity?: string
    addressPostalCode?: string
  }
  // Company requirements (Stripe `identity.business_details` object)
  business?: {
    registeredName?: string
    addressLine1?: string
    addressCity?: string
    addressPostalCode?: string
  }
}

@Component({
  selector: 'app-stripe-settings-dialog',
  templateUrl: './stripe-settings-dialog.component.html',
  styleUrl: './stripe-settings-dialog.component.scss'
})
export class StripeSettingsDialogComponent {
  form: FormGroup
  formSubmitted = false
  loading: boolean

  /** The KYC fields required for the currently selected country + business type. */
  requiredFields = new Set<string>()

  private readonly numericFields = new Set(['addressPostalCode'])

  constructor(
    public dialogRef: MatDialogRef<StripeSettingsDialogComponent>,
    private fb: FormBuilder,
    private elementRef: ElementRef<HTMLElement>,
    @Inject(MAT_DIALOG_DATA) public data: StripeSettingsDialogData
  ) {
    // Default the mode from whatever was actually saved before: an existing Connect
    // onboarding (country/businessType present) wins, then an existing BYOK key,
    // otherwise BYOK — the simpler, zero-eligibility-requirements path — for a
    // merchant who's never configured Stripe on this project at all.
    const initialMode: StripeConnectionMode =
      data.connectionMode || (data.country || data.businessType ? 'connect' : 'byok')

    this.form = this.fb.group({
      connectionMode: [initialMode, Validators.required],
      businessType: [data.businessType || 'individual', Validators.required],
      country: [data.country || '', Validators.required],
      stripApiKey: [data.stripApiKey || ''],
      stripePublishableKey: [data.stripePublishableKey || ''],
      stripeWebhookSecret: [data.stripeWebhookSecret || ''],
      individual: this.fb.group({
        givenName: [data.individual?.givenName || ''],
        surname: [data.individual?.surname || ''],
        dateOfBirth: [data.individual?.dateOfBirth || ''],
        addressLine1: [data.individual?.addressLine1 || ''],
        addressCity: [data.individual?.addressCity || ''],
        addressPostalCode: [data.individual?.addressPostalCode || '']
      }),
      business: this.fb.group({
        registeredName: [data.business?.registeredName || ''],
        addressLine1: [data.business?.addressLine1 || ''],
        addressCity: [data.business?.addressCity || ''],
        addressPostalCode: [data.business?.addressPostalCode || '']
      })
    })
    this.loading = !!data.isLoading

    this.recomputeRequiredFields()
    this.form.get('connectionMode')?.valueChanges.subscribe(() => this.recomputeRequiredFields())
    this.form.get('businessType')?.valueChanges.subscribe(() => this.recomputeRequiredFields())
    this.form.get('country')?.valueChanges.subscribe(() => this.recomputeRequiredFields())
  }

  get isByokMode(): boolean {
    return this.form.get('connectionMode')?.value === 'byok'
  }

  get isConnectMode(): boolean {
    return this.form.get('connectionMode')?.value === 'connect'
  }

  setMode(mode: StripeConnectionMode): void {
    this.form.get('connectionMode')?.setValue(mode)
  }

  /**
   * Which countries offer the given business type — drives the single country
   * <select>'s options, so e.g. AE simply isn't listed while "Individual" is selected.
   */
  availableCountries(businessType: StripeBusinessType): string[] {
    const map: any = stripeConnectRequiredFields
    return Object.keys(map).filter((code) => !!map[code]?.[businessType])
  }

  /**
   * Recomputes which fields are actually required for the current mode, and
   * re-applies validators accordingly:
   *  - 'byok' mode only requires the API key; Connect's country/businessType/KYC
   *    fields are all cleared of validators (and hidden in the template).
   *  - 'connect' mode requires country/businessType plus whichever KYC fields
   *    Stripe asks for at that combination (via stripeConnectRequiredFields);
   *    the API key field is cleared (and hidden).
   * Runs on every mode/businessType/country change, since all three feed the lookup.
   * If the selected country isn't offered for the new businessType (e.g. switching
   * to Individual while AE is selected), the country is reset.
   */
  private recomputeRequiredFields(): void {
    const stripApiKeyControl = this.form.get('stripApiKey')
    const businessTypeControl = this.form.get('businessType')
    const countryControl = this.form.get('country')

    if (this.isByokMode) {
      stripApiKeyControl?.setValidators([Validators.required])
      stripApiKeyControl?.updateValueAndValidity({ emitEvent: false })
      businessTypeControl?.clearValidators()
      businessTypeControl?.updateValueAndValidity({ emitEvent: false })
      countryControl?.clearValidators()
      countryControl?.updateValueAndValidity({ emitEvent: false })
      this.requiredFields = new Set()
      this.clearGroupValidators('individual')
      this.clearGroupValidators('business')
      return
    }

    stripApiKeyControl?.clearValidators()
    stripApiKeyControl?.updateValueAndValidity({ emitEvent: false })
    businessTypeControl?.setValidators([Validators.required])
    businessTypeControl?.updateValueAndValidity({ emitEvent: false })
    countryControl?.setValidators([Validators.required])

    const map: any = stripeConnectRequiredFields
    const businessType: StripeBusinessType = businessTypeControl?.value
    const country: string = countryControl?.value

    if (country && !map[country]?.[businessType]) {
      countryControl?.setValue('', { emitEvent: false })
    }
    countryControl?.updateValueAndValidity({ emitEvent: false })

    const activeCountry: string = countryControl?.value
    this.requiredFields = new Set(map[activeCountry]?.[businessType] ?? [])

    const isIndividual = businessType === 'individual'
    this.applyGroupValidators(isIndividual ? 'individual' : 'business')
    this.clearGroupValidators(isIndividual ? 'business' : 'individual')
  }

  private applyGroupValidators(groupName: 'individual' | 'business'): void {
    const group = this.form.get(groupName)
    for (const name of Object.keys((group as FormGroup)?.controls ?? {})) {
      const control = group?.get(name)
      if (this.requiredFields.has(name)) {
        const validators = this.numericFields.has(name)
          ? [Validators.required, Validators.pattern(/^[0-9]+$/)]
          : [Validators.required]
        control?.setValidators(validators)
      } else {
        control?.clearValidators()
      }
      control?.updateValueAndValidity({ emitEvent: false })
    }
  }

  private clearGroupValidators(groupName: 'individual' | 'business'): void {
    const group = this.form.get(groupName)
    for (const name of Object.keys((group as FormGroup)?.controls ?? {})) {
      const control = group?.get(name)
      control?.clearValidators()
      control?.updateValueAndValidity({ emitEvent: false })
    }
  }

  /** Whether the given KYC field is required (and should be shown) for the current selection. */
  isFieldRequired(name: string): boolean {
    return this.requiredFields.has(name)
  }

  /**
   * Called by the parent once it's fetched+decrypted previously saved settings,
   * to patch them in after the popup has already opened with a skeleton showing.
   */
  applyLoadedData(loaded: Partial<StripeSettingsDialogData> | null): void {
    if (loaded) {
      this.form.patchValue(loaded)
      const mode = loaded.connectionMode || (loaded.country || loaded.businessType ? 'connect' : 'byok')
      this.form.get('connectionMode')?.setValue(mode, { emitEvent: false })
    }
    this.loading = false
    this.recomputeRequiredFields()
  }

  close() {
    this.dialogRef.close()
  }

  save() {
    this.formSubmitted = true
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      this.scrollToFirstInvalidField()
      return
    }
    this.dialogRef.close(this.form.value as StripeSettingsDialogData)
  }

  /**
   * Jumps to and focuses the first invalid control so an invalid Save isn't just a
   * silent no-op — otherwise an error on a field scrolled out of view (e.g. the phone
   * field) is easy to miss, and it just looks like the button did nothing.
   */
  private scrollToFirstInvalidField(): void {
    setTimeout(() => {
      const firstInvalidName = Object.keys(this.form.controls).find((name) => this.form.get(name)?.invalid)
      if (!firstInvalidName) return
      const el = this.elementRef.nativeElement.querySelector(`[formcontrolname="${firstInvalidName}"]`) as HTMLElement | null
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el?.focus()
    })
  }

  disable() {
    this.dialogRef.close('disable')
  }
}
